import I18n from 'I18n';
import Raven from 'raven-js';
import enviro from 'enviro';
import httpNoAuth from 'hub-http/clients/noAuthApiClient';
import { getFullUrl } from 'hubspot-url-utils';
import { na1 } from 'hubspot-url-utils/hublets';
import getUserInfo from 'hub-http/userInfo';
import { PRODUCTION_PQL_FORM_ATTRIBUTES, QA_PQL_FORM_ATTRIBUTES } from '../constants/pql';
import { getIsProdAcceptanceTest } from './acceptanceTest';
import { productUpgradeInterest } from '../constants/productUpgradeInterest';
import { getTrackingProperties, track } from './usageTracker';
import { trackPqlFailure } from './metrics';
export const getPqlFormAttributes = () => enviro.isQa() ? QA_PQL_FORM_ATTRIBUTES : PRODUCTION_PQL_FORM_ATTRIBUTES;
const formHref = getFullUrl('forms', {
  domainOverride: 'hsforms',
  hubletOverride: na1
});
const buildPqlSubmissionFormUrl = ({
  portalId,
  formId
}) => `${formHref}/submissions/v3/integration/submit/${portalId}/${formId}`;

// Using an email address that ends in +skipform results in the form not actually submitting
// data, but resolves successfully. We want to do this for prod acceptance tests to
// prevent polluting 53 data.
export const PROD_ACCEPTANCE_TEST_EMAIL = 'acceptance-test+skipform@hubspot.com';
export const _getEmail = userEmail => {
  if (getIsProdAcceptanceTest()) {
    return PROD_ACCEPTANCE_TEST_EMAIL;
  } else {
    return userEmail;
  }
};
const getScopedProducts = userInfo => {
  const {
    user,
    portal
  } = userInfo;
  const portalScopes = [];
  if (user.scopes.includes('salesaccelerator-access')) {
    portalScopes.push('Sales-Pro');
  } else if (user.scopes.includes('crm-access')) {
    portalScopes.push('Sales-Free');
  }
  if (portal.product_type === 'basic') {
    portalScopes.push('Marketing-Basic');
  } else if (portal.product_type === 'pro') {
    portalScopes.push('Marketing-Pro');
  } else if (portal.product_type === 'enterprise') {
    portalScopes.push('Marketing-Enterprise');
  }
  return portalScopes;
};
const getCommMethodExp = ({
  isAssignable,
  isRetail
}) => {
  // will not have these fields when not coming from a Talk to Sales PQL.
  if (isRetail === undefined && isAssignable === undefined) {
    return 'Not Applicable';
  }
  if (isRetail) {
    return 'Retail';
  }
  if (isAssignable) {
    return 'Assignable';
  }
  return 'Unassignable';
};
export const submitForm = (form, data) => {
  if (enviro.getShort() !== 'prod') {
    console.log('Submission to pql form', form, data);
  }
  return httpNoAuth.post(form, {
    data,
    withCredentials: false
  });
};
export const getFormattedDate = timestamp => {
  const currentDate = new Date(timestamp);
  const date = currentDate.getDate();
  const month = currentDate.getMonth() + 1;
  const year = currentDate.getFullYear();
  const formattedDate = date < 10 ? `0${date}` : date;
  const formattedMonth = month < 10 ? `0${month}` : month;
  return `${formattedMonth}/${formattedDate}/${year}`;
};
const getProductUpgradeInterest = apiName => {
  var _productUpgradeIntere;
  return (_productUpgradeIntere = productUpgradeInterest[apiName]) !== null && _productUpgradeIntere !== void 0 ? _productUpgradeIntere : 'General';
};
const isPhoneNumber = dataFormOptions => {
  return 'phoneNumber' in dataFormOptions;
};
const isEmail = dataFormOptions => {
  return 'emailSubject' in dataFormOptions && 'emailBody' in dataFormOptions;
};
const getFieldDataFromOptions = options => {
  const fieldData = [];
  if (isPhoneNumber(options)) {
    fieldData.push({
      name: 'pql_phone_number_submitted',
      value: true
    }, {
      name: 'phone',
      value: options.phoneNumber
    });
  } else {
    fieldData.push({
      name: 'pql_phone_number_submitted',
      value: false
    });
  }

  // Email fields
  if (isEmail(options)) {
    fieldData.push({
      name: 'pql_message_submitted',
      value: true
    }, {
      name: 'pql_message_type',
      value: options.emailSubject
    }, {
      name: 'pql_message_content',
      value: options.emailBody
    });
  } else {
    fieldData.push({
      name: 'pql_message_submitted',
      value: false
    });
  }
  return fieldData;
};
export const DEFAULT_SUBSCRIPTIONS = [16, 7818550, 4898827, 349169, 1281077, 121, 50894];
export const getLegalConsentOptions = () => {
  const privacyPolicyText = I18n.text('purchaseMotions.legalConsentOptions.privacyPolicy');
  return {
    legitimateInterest: {
      value: true,
      text: privacyPolicyText,
      legalBasis: 'LEAD',
      subscriptionTypeIds: DEFAULT_SUBSCRIPTIONS
    }
  };
};
export const createPqlFormData = ({
  apiName,
  isAssignable,
  isRetail,
  metaData,
  trackingData,
  userInfo,
  options = {}
}) => {
  const portalScopes = getScopedProducts(userInfo);
  const optionsFieldData = getFieldDataFromOptions(options);
  const {
    user,
    portal
  } = userInfo;
  const commMethod = getCommMethodExp({
    isAssignable,
    isRetail
  });
  const formattedDate = getFormattedDate(metaData.timestamp);
  const email = _getEmail(user.email);
  const fieldData = [{
    name: 'email',
    value: email
  }, {
    name: 'firstname',
    value: user.first_name
  }, {
    name: 'lastname',
    value: user.last_name
  }, {
    name: 'crm_hub_id',
    value: portal.portal_id
  }, {
    name: 'pql_conversion_date',
    value: formattedDate
  }, {
    name: 'pql_app',
    value: trackingData.app
  }, {
    name: 'pql_source',
    value: trackingData.uniqueId
  }, {
    name: 'product_signup',
    value: portalScopes.length > 0 ? portalScopes.join(', ') : ''
  }, {
    name: 'product_upgrade_interest',
    value: getProductUpgradeInterest(apiName)
  }, {
    name: 'comm_method_experience',
    value: commMethod
  }, ...optionsFieldData];
  const pqlFormData = {
    fields: fieldData,
    skipValidation: true,
    legalConsentOptions: getLegalConsentOptions()
  };
  return pqlFormData;
};
const getRepName = repInfo => {
  if (repInfo && repInfo.name) {
    return repInfo.name;
  }
  return 'Not assigned';
};

// to use only when additional PQL information is being submitted. i.e. email or phone number
export const submitAdditionalPqlForm = ({
  apiName,
  trackingData,
  options = {}
}) => {
  const formAttributes = getPqlFormAttributes();
  const formEndpoint = buildPqlSubmissionFormUrl(formAttributes);
  const pqlPromise = getUserInfo().then(userInfo => {
    const metaData = {
      timestamp: Date.now()
    };
    const pqlFormData = createPqlFormData({
      apiName,
      metaData,
      trackingData,
      userInfo,
      options
    });
    return submitForm(formEndpoint, pqlFormData);
  });
  pqlPromise.catch(error => {
    Raven.captureException(error);
  });
  return pqlPromise;
};
export const submitPql = ({
  apiName,
  isRetail,
  isAssignable,
  repInfo,
  trackingData,
  upgradeConfig
}) => {
  const formAttributes = getPqlFormAttributes();
  const formEndpoint = buildPqlSubmissionFormUrl(formAttributes);
  const pqlPromise = getUserInfo().then(userInfo => {
    const metaData = {
      timestamp: Date.now()
    };
    const pqlFormData = createPqlFormData({
      apiName,
      metaData,
      isAssignable,
      isRetail,
      trackingData,
      userInfo
    });
    return submitForm(formEndpoint, pqlFormData);
  }).then(() => {
    // ********** PUBLIC EVENT **********
    // Public Events help teams across HubSpot automate work and customize experiences based on user actions.
    // Speak with #product-insight and your PM before any shipping any changes to this event incl. event name, properties, values, and when it occurs.
    // Read more about Public Events on the wiki: https://wiki.hubspotcentral.net/display/PM/Public+Events+-+Amplitude+events+ready+for+HubSpot+team+use+and+automation
    track('pql', Object.assign({
      action: 'submission succeeded'
    }, getTrackingProperties({
      apiName,
      trackingData,
      upgradeConfig,
      sourceKey: 'TALK_TO_SALES'
    }), {
      isRetail,
      isAssignable,
      repName: getRepName(repInfo)
    }));
  });
  pqlPromise.catch(error => {
    // ********** PUBLIC EVENT **********
    // Public Events help teams across HubSpot automate work and customize experiences based on user actions.
    // Speak with #product-insight and your PM before any shipping any changes to this event incl. event name, properties, values, and when it occurs.
    // Read more about Public Events on the wiki: https://wiki.hubspotcentral.net/display/PM/Public+Events+-+Amplitude+events+ready+for+HubSpot+team+use+and+automation
    track('pql', Object.assign({
      action: 'submission failed'
    }, getTrackingProperties({
      apiName,
      trackingData,
      upgradeConfig,
      sourceKey: 'TALK_TO_SALES'
    }), {
      isRetail,
      isAssignable,
      repName: getRepName(repInfo)
    }));
    trackPqlFailure(trackingData.app);
    Raven.captureException(error);
  });
  return pqlPromise;
};